// The structured steps a person can fill in with a form instead of free text,
// such as sending a Slack message: what each takes and how it reads in the
// skill's markdown. The markdown only says what to do, in plain words. The agent
// finds the skills and tools that do it. How a form shows each step lives in
// packages/app. No database or Node imports, so packages/app can import this.

import { z } from "zod/v4";

const MAX_FIELD_LENGTH = 10_000;

const text = z.string().max(MAX_FIELD_LENGTH);

// A required field's title is how a warning names it when it is empty.
const required = (title: string, description: string) =>
  text.meta({ title, description });

const attachments = text
  .optional()
  .describe("What to attach, in plain words, such as the PDF of the report.");

const slackCall = z.object({
  type: z.literal("slack_message"),
  channels: required(
    "Slack recipients",
    "Slack channel ids separated by commas, or {{name}} for an input that holds them.",
  ),
  message: required("Message", "What to post. It can use {{references}}."),
  attachments,
});

const emailCall = z.object({
  type: z.literal("email"),
  to: required(
    "Email recipients",
    "Email addresses separated by commas, or {{name}} for an input that holds them.",
  ),
  subject: required("Subject", "The subject line."),
  body: required("Body", "What the email says. It can use {{references}}."),
  attachments,
});

const documentCall = z.object({
  type: z.literal("document"),
  fileType: required(
    "File type",
    "The kind of Library file to create, such as Document or Presentation.",
  ),
  name: required(
    "Name",
    "The file's name. It can use {{references}}, such as {{week}}.",
  ),
  content: required("What it covers", "What the file should cover."),
  folder: text
    .optional()
    .describe("The Library folder to save it in, if not the top level."),
  project: text
    .optional()
    .describe("The project to attach it to, such as {{project}}."),
});

/** A step's structured call: one kind of step and its fields. */
export const stepCallSchema = z.discriminatedUnion("type", [
  slackCall,
  emailCall,
  documentCall,
]);

export type StepCall = z.infer<typeof stepCallSchema>;

export type StepType = StepCall["type"];

/** A call of `name` with nothing filled in, which a new step of that kind starts from. */
export function emptyStepCall(name: StepType): StepCall {
  switch (name) {
    case "slack_message":
      return { type: name, channels: "", message: "" };
    case "email":
      return { type: name, to: "", subject: "", body: "" };
    case "document":
      return { type: name, fileType: "Document", name: "", content: "" };
  }
}

/** The recipients in a comma-separated list, without blanks. */
export function splitList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/** A call in words: what to do, and the free text it carries, which reads apart from it. */
export type RenderedCall = { sentence: string; quote: string };

const trimmed = (value: string | undefined) => value?.trim() ?? "";

// What to attach, as a closing sentence.
const attach = (what: string | undefined) =>
  trimmed(what) ? ` Attach ${trimmed(what)}.` : "";

/** A call as the skill's markdown says it: a sentence naming no tool, and the free text to quote under it. */
export function renderStepCall(call: StepCall): RenderedCall {
  switch (call.type) {
    case "slack_message": {
      const channels = splitList(call.channels).join(", ");
      const message = trimmed(call.message);
      return {
        sentence: `Send a Slack message${channels ? ` to ${channels}` : ""}${message ? ", with this message" : ""}.${attach(call.attachments)}`,
        quote: message,
      };
    }
    case "email": {
      const to = splitList(call.to).join(", ");
      const subject = trimmed(call.subject);
      const body = trimmed(call.body);
      return {
        sentence: `Send an email${to ? ` to ${to}` : ""}${subject ? `, subject ${JSON.stringify(subject)}` : ""}${body ? ", with this body" : ""}.${attach(call.attachments)}`,
        quote: body,
      };
    }
    case "document": {
      const name = trimmed(call.name);
      const folder = trimmed(call.folder);
      const project = trimmed(call.project);
      const content = trimmed(call.content);
      return {
        sentence: `Create a Library file of type ${trimmed(call.fileType) || "Document"}${name ? ` named ${JSON.stringify(name)}` : ""}${folder ? `, in the folder ${folder}` : ""}${project ? `, attached to project ${project}` : ""}${content ? ", covering this" : ""}.`,
        quote: content,
      };
    }
  }
}

/** A call with each of its text fields passed through `map`. */
export function mapStepCallValues(
  call: StepCall,
  map: (value: string) => string,
): StepCall {
  const mapped = Object.fromEntries(
    Object.entries(call).map(([field, value]) => [
      field,
      field !== "type" && typeof value === "string" ? map(value) : value,
    ]),
  );
  const parsed = stepCallSchema.safeParse(mapped);
  return parsed.success ? parsed.data : call;
}

/** What a call's fields hold, for finding the `{{references}}` in them. */
export function stepCallValues(call: StepCall): string[] {
  const { type: _type, ...fields } = call;
  return Object.values(fields).filter(
    (value): value is string => typeof value === "string",
  );
}

/**
 * The titles of the fields a call leaves empty. A field is required when its
 * schema is a text field that isn't optional.
 */
export function emptyRequiredFields(call: StepCall): string[] {
  const schema = stepCallSchema.options.find(
    (option) => option.shape.type.value === call.type,
  );
  if (!schema) return [];
  const values: Record<string, unknown> = call;
  return Object.entries(schema.shape).flatMap(([field, type]) => {
    const value = values[field];
    const isEmpty = typeof value !== "string" || !value.trim();
    const title = z.globalRegistry.get(type)?.title;
    return title !== undefined && isEmpty ? [title] : [];
  });
}
